import React, { useState, useEffect, useRef } from 'react';
import { getCurrentAuthenticatedUsername, signOutCurrentUser } from './cognitoAuth';

const menuItems = [
  { label: 'Home', href: '/' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '/contact' },
  {
    label: 'My Stores',
    // child links rendered in a dropdown
    children: [
      { label: 'BrickLink', href: 'https://store.bricklink.com/DerekDowns#/shop', external: true },
    ],
  },
];

function NavMenu() {
  const [barVisible, setBarVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [authUsername, setAuthUsername] = useState('');
  const [authMenuOpen, setAuthMenuOpen] = useState(false);
  const authMenuRef = useRef(null);

  // Scroll to top detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      
      // Auto-show nav when scrolling back to top
      if (scrollTop <= 10 && !barVisible) {
        setBarVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [barVisible]);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 1024);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadAuthUser() {
      const username = await getCurrentAuthenticatedUsername();
      if (isMounted) {
        setAuthUsername(username || '');
      }
    }

    loadAuthUser();
    window.addEventListener('focus', loadAuthUser);
    window.addEventListener('auth-profile-updated', loadAuthUser);

    return () => {
      isMounted = false;
      window.removeEventListener('focus', loadAuthUser);
      window.removeEventListener('auth-profile-updated', loadAuthUser);
    };
  }, []);

  useEffect(() => {
    function handleDocumentClick(event) {
      if (authMenuRef.current && !authMenuRef.current.contains(event.target)) {
        setAuthMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, []);

  function handleAuthAction() {
    if (authUsername) {
      setAuthMenuOpen((current) => !current);
      return;
    }

    window.location.assign('/login');
  }

  function handleLogout() {
    signOutCurrentUser();
    setAuthUsername('');
    setAuthMenuOpen(false);
    window.location.assign('/login');
  }

  function handleOpenSettings() {
    setAuthMenuOpen(false);
    window.location.assign('/settings');
  }

  // Removed padding effect - sticky positioning handles layout naturally

  if (!isMobile) {
    return (
      <>
        {/* Full Navigation Bar (when visible) */}
        {barVisible && (
          <>
            <nav className="sticky-nav top-icons-bar" style={{ 
              position: 'sticky', 
              top: 0, 
              zIndex: 101, 
              background: '#111', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.7)', 
              minHeight: '48px', 
              width: '100%', 
              display: 'flex',
              alignItems: 'center', 
              justifyContent: 'flex-end', 
              padding: '0 2rem' 
            }}>
              <div className="nav-icons" style={{ display: 'flex', alignItems: 'center' }}>
                <div ref={authMenuRef} style={{ position: 'relative', marginRight: '1rem' }}>
                  <button
                    type="button"
                    onClick={handleAuthAction}
                    className="auth-link no-bg"
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '1rem',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.6rem',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    {authUsername && (
                      <span style={{ fontSize: '0.95rem', fontWeight: 600, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {authUsername}
                      </span>
                    )}
                    <i className="fas fa-user" style={{ fontSize: '1.5rem' }}></i>
                  </button>
                  {authUsername && authMenuOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 'calc(100% + 10px)',
                        minWidth: '180px',
                        background: '#111',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: '10px',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
                        padding: '0.5rem',
                      }}
                    >
                      <div style={{ color: '#bfbfbf', fontSize: '0.85rem', padding: '0.45rem 0.75rem' }}>Signed in as</div>
                      <div style={{ color: '#fff', fontSize: '0.95rem', fontWeight: 700, padding: '0 0.75rem 0.65rem' }}>{authUsername}</div>
                      <button
                        type="button"
                        onClick={handleOpenSettings}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          background: 'transparent',
                          border: 'none',
                          color: '#fff',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.95rem',
                        }}
                      >
                        User settings
                      </button>
                      <button
                        type="button"
                        onClick={handleLogout}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          background: 'transparent',
                          border: 'none',
                          color: '#fff',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.95rem',
                        }}
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
                <a href="/cart" className="auth-link no-bg" style={{ fontSize: '1.5rem', color: '#fff', display: 'flex', alignItems: 'center' }}>
                  <i className="fas fa-shopping-cart"></i>
                </a>
              </div>
            </nav>
            <nav className="sticky-nav main-menu-bar" style={{ 
              position: 'sticky', 
              top: 48, 
              zIndex: 100, 
              background: '#111', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.7)', 
              minHeight: '64px', 
              width: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              padding: '0 2rem'
            }}>
              <a href="/" className="logo" style={{ display: 'flex', alignItems: 'center', marginRight: '2rem' }}>
                <img src="/img/resumeLogo.png" alt="Logo" style={{ width: '160px', transition: 'width 0.3s' }} />
              </a>
              <ul className="menu" style={{ display: 'flex', alignItems: 'center', listStyle: 'none', margin: 0, padding: 0 }}>
                {menuItems.map((item, idx) => (
                  <li
                    className="item"
                    key={idx}
                    style={{ margin: '0 1rem', position: 'relative' }}
                  >
                      {item.children ? (
                      <>
                        <button
                          type="button"
                          aria-haspopup="true"
                          aria-expanded={openDropdown === idx}
                          onClick={() => setOpenDropdown((o) => (o === idx ? null : idx))}
                          className="nav-link-button"
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#fff',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            padding: '0.5rem 1rem',
                            borderRadius: '4px',
                            fontWeight: 500,
                            fontFamily: 'inherit',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'color 0.2s ease'
                          }}
                        >
                          {item.label}
                          <i className="fas fa-caret-down" style={{ marginLeft: '6px', fontSize: '1.5rem', transform: openDropdown === idx ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 180ms ease', display: 'inline-block' }}></i>
                        </button>
                        <ul
                          className="submenu"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            background: '#111',
                            padding: '0.25rem',
                            listStyle: 'none',
                            margin: 0,
                            borderRadius: '6px',
                            boxShadow: '0 6px 18px rgba(0,0,0,0.6)',
                            minWidth: '180px',
                            zIndex: 200,
                            transformOrigin: 'top',
                            transform: openDropdown === idx ? 'scaleY(1)' : 'scaleY(0)',
                            opacity: openDropdown === idx ? 1 : 0,
                            transition: 'transform 180ms ease, opacity 180ms ease',
                            pointerEvents: openDropdown === idx ? 'auto' : 'none',
                          }}
                        >
                            {item.children.map((child, cidx) => (
                              <li key={cidx} style={{ padding: '0.25rem 0' }}>
                                <a href={child.href} target={child.external ? '_blank' : undefined} rel={child.external ? 'noopener noreferrer' : undefined} className="nav-link" style={{ color: '#fff', textDecoration: 'none', display: 'block', padding: '0.5rem 1rem', borderRadius: '4px', fontSize: '1.5rem', fontFamily: 'inherit', fontWeight: 500, textAlign: 'center', transition: 'color 0.2s ease' }}>
                                  {child.label}
                                </a>
                              </li>
                            ))}
                        </ul>
                      </>
                      ) : (
                      <a href={item.href} target={item.external ? '_blank' : undefined} rel={item.external ? 'noopener noreferrer' : undefined} className="nav-link" style={{ color: '#fff', textDecoration: 'none', padding: '0.5rem 1rem', borderRadius: '5px', fontWeight: 500, fontFamily: 'inherit', fontSize: '1.5rem', transition: 'color 0.2s ease' }}>
                        {item.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                aria-label="Hide navigation bar"
                style={{ 
                  position: 'absolute',
                  right: '2rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer', 
                  fontSize: '2rem', 
                  color: '#fff', 
                  outline: 'none'
                }}
                onClick={() => setBarVisible(false)}
              >
                <i className="fas fa-chevron-up"></i>
              </button>
            </nav>
          </>
        )}

        {/* Minimized Bar (arrow only, far right, no background) */}
        {!barVisible && (
          <div style={{ 
            position: 'fixed', 
            top: '48px', 
            right: '20px', 
            zIndex: 102, 
            width: 'auto',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            background: 'transparent'
          }}>
            <button
              type="button"
              aria-label="Show navigation bar"
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer', 
                fontSize: '3rem', 
                color: '#fff',
                WebkitTextStroke: '2px black',
                outline: 'none',
                padding: '5px',
                transition: 'all 0.3s ease'
              }}
              onClick={() => setBarVisible(true)}
            >
              <i className="fas fa-chevron-down"></i>
            </button>
          </div>
        )}
      </>
    );
  }

  // Mobile layout
  return (
    <>
      <nav className="sticky-nav top-icons-bar" style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        zIndex: 101, 
        background: '#111', 
        width: '100%', 
        minHeight: '56px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.7)', 
        padding: '0 1rem' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button
            type="button"
            aria-label="Open menu"
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer', 
              fontSize: '2rem', 
              color: '#fff', 
              outline: 'none', 
              marginRight: '1rem' 
            }}
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            <i className="fas fa-bars"></i>
          </button>
        </div>
        <div className="nav-icons" style={{ display: 'flex', alignItems: 'center' }}>
          <div ref={authMenuRef} style={{ position: 'relative', marginRight: '1rem' }}>
            <button
              type="button"
              onClick={handleAuthAction}
              className="auth-link no-bg"
              style={{
                background: 'none',
                border: 'none',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              {authUsername && (
                <span style={{ fontSize: '0.85rem', fontWeight: 600, maxWidth: '96px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {authUsername}
                </span>
              )}
              <i className="fas fa-user" style={{ fontSize: '1.5rem' }}></i>
            </button>
            {authUsername && authMenuOpen && (
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 'calc(100% + 10px)',
                  minWidth: '160px',
                  background: '#111',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '10px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
                  padding: '0.5rem',
                }}
              >
                <div style={{ color: '#bfbfbf', fontSize: '0.8rem', padding: '0.45rem 0.75rem' }}>Signed in as</div>
                <div style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 700, padding: '0 0.75rem 0.65rem' }}>{authUsername}</div>
                <button
                  type="button"
                  onClick={handleOpenSettings}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    background: 'transparent',
                    border: 'none',
                    color: '#fff',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                  }}
                >
                  User settings
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    background: 'transparent',
                    border: 'none',
                    color: '#fff',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                  }}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
          <a href="/cart" className="auth-link no-bg" style={{ fontSize: '1.5rem', color: '#fff', display: 'flex', alignItems: 'center' }}>
            <i className="fas fa-shopping-cart"></i>
          </a>
        </div>
      </nav>
      {mobileMenuOpen && (
        <div className="mobile-dropdown-push" style={{ 
          width: '100%', 
          background: '#111', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.7)', 
          padding: '2rem 1rem', 
          position: 'relative', 
          zIndex: 99, 
          minHeight: '180px', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          marginTop: '56px' 
        }}>
          <a href="/" className="logo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
            <img src="/img/resumeLogo.png" alt="Logo" style={{ width: '160px', verticalAlign: 'middle', margin: '0 auto', display: 'block' }} />
          </a>
          <ul className="mobile-dropdown" style={{ background: 'transparent', listStyle: 'none', margin: 0, padding: 0, width: '100%' }}>
            {menuItems.map((item, idx) => (
              <li className="item" key={idx} style={{ margin: '0.5rem 0', textAlign: 'center', width: '100%' }}>
                {item.children ? (
                  <div style={{ width: '100%' }}>
                    <button
                      type="button"
                      onClick={() => setOpenDropdown((o) => (o === idx ? null : idx))}
                      style={{
                        width: '100%',
                        textAlign: 'center',
                        color: '#fff',
                        background: 'none',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        fontSize: '1.5rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 500,
                        transition: 'color 0.2s ease'
                      }}
                    >
                      <span>{item.label}</span>
                      <i className="fas fa-caret-down" style={{ marginLeft: '6px', transform: openDropdown === idx ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 180ms ease', fontSize: '1.5rem' }}></i>
                    </button>
                    <div style={{ overflow: 'hidden', transformOrigin: 'top', transform: openDropdown === idx ? 'scaleY(1)' : 'scaleY(0)', opacity: openDropdown === idx ? 1 : 0, transition: 'transform 180ms ease, opacity 180ms ease' }}>
                      {item.children.map((child, cidx) => (
                        <a key={cidx} href={child.href} target={child.external ? '_blank' : undefined} rel={child.external ? 'noopener noreferrer' : undefined} className="nav-link" style={{ width: '100%', textAlign: 'center', color: '#fff', background: 'none', border: 'none', padding: '0.5rem 1rem', fontSize: '1.5rem', borderRadius: '4px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontWeight: 500, transition: 'color 0.2s ease' }}>
                          {child.label}
                        </a>
                      ))}
                    </div>
                  </div>
                    ) : (
                  <a href={item.href} target={item.external ? '_blank' : undefined} rel={item.external ? 'noopener noreferrer' : undefined} className="nav-link" style={{ width: '100%', textAlign: 'center', color: '#fff', background: 'none', border: 'none', padding: '0.5rem 1rem', fontSize: '1.5rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontWeight: 500, fontFamily: 'inherit', transition: 'color 0.2s ease' }}>
                    {item.label}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

export default NavMenu;